import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ExportMyPhotosInput,
  ExportMyPhotosResult,
  PhotoExportJob,
  PhotoExportPart,
} from '@citizen-walk/shared-types';
import { auth, db, functions } from '../../lib/firebase';

/**
 * exportMyPhotos hook (Feature: 自分の投稿写真を分割 ZIP で一括 download — 非同期ジョブ版)。
 *
 * Pattern C (issue #28): callable はジョブを登録して `jobId` を即返すだけ。実際の分割 ZIP 生成は
 * サーバ側ワーカーが進める。本 hook は callable を呼んだ後、job doc
 * `participants/{uid}/photoExports/{jobId}` を `onSnapshot` で購読し、進捗とパート別 DL リンクを state
 * に反映する。途中で modal を閉じても生成は継続するため、jobId を localStorage に保持し再開できる。
 */
export type ExportMyPhotosState =
  | { status: 'idle' }
  | { status: 'enqueuing' }
  | {
      status: 'running';
      jobId: string;
      totalPhotos: number;
      processedPhotos: number;
      parts: PhotoExportPart[];
    }
  | {
      status: 'done';
      jobId: string;
      totalPhotos: number;
      parts: PhotoExportPart[];
      truncated: boolean;
    }
  | { status: 'error'; error: Error };

export interface UseExportMyPhotosReturn {
  state: ExportMyPhotosState;
  /** ジョブを登録し、進捗の購読を開始する。 */
  call: (input: ExportMyPhotosInput) => Promise<ExportMyPhotosResult | null>;
  /** localStorage に保存した進行中 / 完了ジョブがあれば購読を再開する (modal を開き直したとき用)。 */
  attach: (districtId: string) => boolean;
  /** 購読を解除し idle に戻す (localStorage は消さない = 再開可能)。 */
  reset: () => void;
}

function storageKey(districtId: string): string {
  const uid = auth.currentUser?.uid ?? 'anon';
  return `cw_photo_export:${uid}:${districtId}`;
}

function readStoredJobId(districtId: string): string | null {
  try {
    return window.localStorage?.getItem(storageKey(districtId)) ?? null;
  } catch {
    return null;
  }
}

function writeStoredJobId(districtId: string, jobId: string | null): void {
  try {
    if (jobId === null) window.localStorage?.removeItem(storageKey(districtId));
    else window.localStorage?.setItem(storageKey(districtId), jobId);
  } catch {
    /* localStorage 不可 (プライベートモード等) でも致命的ではない */
  }
}

export function useExportMyPhotos(): UseExportMyPhotosReturn {
  const [state, setState] = useState<ExportMyPhotosState>({ status: 'idle' });
  const unsubRef = useRef<Unsubscribe | null>(null);

  const stop = useCallback(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
  }, []);

  const subscribe = useCallback(
    (uid: string, jobId: string, districtId: string) => {
      stop();
      const ref = doc(db, `participants/${uid}/photoExports/${jobId}`);
      unsubRef.current = onSnapshot(
        ref,
        (snap) => {
          if (!snap.exists()) {
            // 期限切れ / 掃除済み等で doc が無い → idle に戻し保存も消す。
            writeStoredJobId(districtId, null);
            setState({ status: 'idle' });
            return;
          }
          const job = snap.data() as PhotoExportJob;
          const parts = Array.isArray(job.parts) ? job.parts : [];
          if (job.status === 'failed') {
            writeStoredJobId(districtId, null);
            setState({
              status: 'error',
              error: new Error(job.error ?? '写真のまとめ作成に失敗しました。'),
            });
          } else if (job.status === 'done') {
            setState({
              status: 'done',
              jobId,
              totalPhotos: job.totalPhotos ?? 0,
              parts,
              truncated: Boolean(job.truncated),
            });
          } else {
            setState({
              status: 'running',
              jobId,
              totalPhotos: job.totalPhotos ?? 0,
              processedPhotos: job.processedPhotos ?? 0,
              parts,
            });
          }
        },
        (err) => {
          setState({ status: 'error', error: err });
        },
      );
    },
    [stop],
  );

  const call = useCallback(
    async (input: ExportMyPhotosInput): Promise<ExportMyPhotosResult | null> => {
      const uid = auth.currentUser?.uid;
      if (!uid) {
        setState({ status: 'error', error: new Error('サインインの準備中です。') });
        return null;
      }
      setState({ status: 'enqueuing' });
      try {
        const fn = httpsCallable<ExportMyPhotosInput, ExportMyPhotosResult>(
          functions,
          'exportMyPhotos',
        );
        const res = await fn(input);
        const { jobId } = res.data;
        writeStoredJobId(input.districtId, jobId);
        subscribe(uid, jobId, input.districtId);
        return res.data;
      } catch (e) {
        setState({ status: 'error', error: e as Error });
        return null;
      }
    },
    [subscribe],
  );

  const attach = useCallback(
    (districtId: string): boolean => {
      const uid = auth.currentUser?.uid;
      const jobId = readStoredJobId(districtId);
      if (!uid || !jobId) return false;
      subscribe(uid, jobId, districtId);
      return true;
    },
    [subscribe],
  );

  const reset = useCallback(() => {
    stop();
    setState({ status: 'idle' });
  }, [stop]);

  // unmount 時にリスナーを必ず解除。
  useEffect(() => () => stop(), [stop]);

  return { state, call, attach, reset };
}
